// ─────────────────────────────────────────────────────────────────────────────
// Lootbox — drop logic for SET 1 "Prismara Awakening"
// ─────────────────────────────────────────────────────────────────────────────

import { CARDS, cardsByRarity } from "../data/cardsData.js";

// ── Region → element mapping ─────────────────────────────────────────────────
const REGION_ELEMENTS = {
  volcano: "fire",
  storm:   "electric",
  ocean:   "water",
  earth:   "earth",
  psychic: "aether",
};

// Drop weights: 70% region element, 25% any other Prismara element, 5% corruption
const REGION_WEIGHT   = 70;
const OFF_REGION_WEIGHT = 25;
const CORRUPTION_WEIGHT = 5;

// ── Slot definitions ───────────────────────────────────────────────────────────
// Slot 1: Common guaranteed
// Slot 2: Common (70%) | Rare (30%)
// Slot 3: Rare (50%) | Epic (35%) | Legendary (15%)
const SLOT_TABLES = [
  [
    { rarity: "common",    weight: 100 },
  ],
  [
    { rarity: "common",    weight: 70  },
    { rarity: "rare",      weight: 30  },
  ],
  [
    { rarity: "rare",      weight: 50  },
    { rarity: "epic",      weight: 35  },
    { rarity: "legendary", weight: 15  },
  ],
];

/**
 * Weighted random pick from a table.
 * @param {{ rarity: string, weight: number }[]} table
 */
function pickRarity(table) {
  const total = table.reduce((s, e) => s + e.weight, 0);
  let roll = Math.random() * total;
  for (const entry of table) {
    roll -= entry.weight;
    if (roll <= 0) return entry.rarity;
  }
  return table[table.length - 1].rarity;
}

/**
 * Pick a random card of the given rarity, weighted by region.
 * - 70% chance: card from the region's element
 * - 25% chance: card from any other non-corruption element
 * -  5% chance: corruption card
 * Falls back gracefully if a pool is empty.
 */
function pickCard(rarity, regionId) {
  const pool = cardsByRarity(rarity);
  if (!pool.length) return CARDS[Math.floor(Math.random() * CARDS.length)];

  const regionElement = REGION_ELEMENTS[regionId];

  // No region info — pure random (backwards compatible)
  if (!regionElement) {
    return pool[Math.floor(Math.random() * pool.length)];
  }

  const regionPool     = pool.filter(c => c.element === regionElement);
  const corruptionPool = pool.filter(c => c.element === "corruption");
  const offRegionPool  = pool.filter(c => c.element !== regionElement && c.element !== "corruption");

  // Weighted roll
  const roll = Math.random() * 100;

  if (roll < REGION_WEIGHT && regionPool.length > 0) {
    return regionPool[Math.floor(Math.random() * regionPool.length)];
  } else if (roll < REGION_WEIGHT + OFF_REGION_WEIGHT && offRegionPool.length > 0) {
    return offRegionPool[Math.floor(Math.random() * offRegionPool.length)];
  } else if (corruptionPool.length > 0) {
    return corruptionPool[Math.floor(Math.random() * corruptionPool.length)];
  }

  // Fallback: any card from pool
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Generate a lootbox drop: 3 cards, one per slot.
 * @param {string} [regionId] — current region for weighted drops
 * @returns {{ card: CardDef, rarity: string, slot: number }[]}
 */
export function generateLootbox(regionId) {
  return SLOT_TABLES.map((table, i) => {
    const rarity = pickRarity(table);
    const card   = pickCard(rarity, regionId);
    return { card, rarity, slot: i + 1 };
  });
}

/**
 * Generate a "Boss Lootbox" — slightly better odds.
 * Slot 1: Rare guaranteed. Slot 2: Rare/Epic. Slot 3: Epic/Legendary.
 * @param {string} [regionId] — current region for weighted drops
 */
export function generateBossLootbox(regionId) {
  const BOSS_TABLES = [
    [{ rarity: "rare",      weight: 100 }],
    [{ rarity: "rare",      weight: 60  }, { rarity: "epic", weight: 40 }],
    [{ rarity: "epic",      weight: 70  }, { rarity: "legendary", weight: 30 }],
  ];
  return BOSS_TABLES.map((table, i) => {
    const rarity = pickRarity(table);
    const card   = pickCard(rarity, regionId);
    return { card, rarity, slot: i + 1 };
  });
}
